import classNames from 'classnames'
import { isPlainObject, kebabCase, mapKeys, memoize, random, throttle } from 'lodash-es'
import platform from './platform'

export { default as Alerts } from './Alerts'
export { default as FMT } from './fmt'
export * from './colors'
export * from './date'
export * from './form'
export * from './hooks'
export * from './jwt'
export * from './notification'
export * from './time'
export { default as Locale } from './Locale'
export { default as platform } from './platform'
export { default as pushWrapper } from './pushWrapper'
export { default as PumpkinTimer } from './PumpkinTimer'
export { default as WakeLock } from './WakeLock'
export { default as StarPrinters } from './StarPrinters'
export { default as SupportChat } from './chat/SupportChat'

/**
 * Simplification over the classnames package.
 * It also converts camelCase object keys into commonly-used kebab-case CSS classes :)
 * @example `<em {...klass('always-on-class', { maybeOnClass: !!shouldBeEnabled })}>bla</em>`
 */
export function klass(...attr) {
	attr = attr.map(entry => {
		if (isPlainObject(entry)) {
			return mapKeys(entry, (v, key) => key.split(' ').map(kebabCase).join(' ').replace(/-(\d)/, '$1'))
		}
		return entry
	})

	const className = classNames(...attr)
	return className? { className } : {}
}

export function randomId(prefix) {
	let rand
	do {
		rand = random(Number.MAX_SAFE_INTEGER)
	} while (randomId.USED.indexOf(rand) >= 0)
	randomId.USED.push(rand)
	return `${prefix}-${rand}`
}
randomId.USED = []

/**
 * Returns the current React or Cordova version.
 * If no argument is given, will either return the version hash from main.xyz.js or "unrecognized", when it's not found (TODO when does it happen?)
 * May also return "WDS-initial-build" in case of the initial refresh of WDS - following hot-updates would have a hash.
 * @param cordova {boolean} Returns the native Cordova app version, if available - false otherwise.
 * @return {string|false|'unrecognized'|'WDS-initial-build'}
 */
export function currentVersion(cordova = false) {
	if (cordova) {
		if ('CORDOVA_VERSION' in window) {
			const [,version] = window.CORDOVA_VERSION.split(':')
			return version || false
		}
		return false
	}

	const script = document.querySelector('head script[src*="/main."], body > script[src*="/main."]')
	let build = script? script.src.match(/main\.(\w*)\./)[1] : 'unrecognized'
	build     = build == 'chunk'? 'WDS-initial' : build.substring(0, 6)
	return `${process.env.REACT_APP_VERSION}-${build}`
}

export const adminURL = form => `${window.ROOT_URL}/admin/?form=${form}`

/**
 * Absolute URL for something stored at /public - mostly sound/image statics.
 * @param path {string} A path relative to React/manager/public
 * @returns {string}
 */
export const publicURL = path => `${platform.isDev()? '': window.ROOT_URL}${process.env.PUBLIC_URL}/${path}`

/**
 * Throttles a function based on its arguments, but avoids calling it twice (start and end of timeout) as the default {@link throttle}.
 * @param func {function} Whatever you want to throttle...
 * @param wait {int} ...and for how many msec
 * @param cacheResolver {function} Receives the arguments and should return a cache key, which is used to skip running the function again during the throttling time
 * @see https://github.com/lodash/lodash/issues/2403#issuecomment-290760787
 */
export const memoizedThrottle = (func, wait, cacheResolver) => {
	//tried home-made first, but after thinking a bit more, it seemed more complex than I wanted to, so I copy-pasta'd
	const memoized = memoize(() => throttle(func, wait, { trailing: false }), cacheResolver)
	return function () {
		memoized.apply(this, arguments).apply(this, arguments) //don't ask me
	}
}

//FIXME move all those file-related functions to a file.js
/**
 * @see https://gist.github.com/borismus/1032746
 * @param dataURI {string}
 */
export function dataURIToBinary(dataURI) {
	const MARKER = ';base64,'
	const base64Index = dataURI.indexOf(MARKER) + MARKER.length
	const base64      = dataURI.substring(base64Index)
	const raw         = window.atob(base64)
	const rawLength = raw.length
	const array     = new Uint8Array(new ArrayBuffer(rawLength))

	for(let i = 0; i < rawLength; i++) {
		array[i] = raw.charCodeAt(i)
	}
	return array
}

/** @typedef FileEntry
 * This comes from Cordova :)
 * @property {string} nativeURL
 * @property {function} remove
 */
/**
 * Writes a blob to a local file.
 * This is Cordova-only, but returns a mock in case of a web+dev platform.
 * @param filename {string}
 * @param blob {Blob}
 * @param onSuccess {function(FileEntry)} final callback
 * @param cacheFolder {boolean}
 */
export function saveFile(filename, blob, onSuccess, cacheFolder = true) {
	return getFile(filename, cacheFolder, {
			nativeURL: `/fake/path/${filename}`,
			createWriter: () => onSuccess({ remove: () => true })
		},
		fileEntry => {
			fileEntry.createWriter(fileWriter => {
				fileWriter.onwriteend = () => onSuccess(fileEntry)
				fileWriter.onerror    = e => onFSError('Error storing ' + filename, { e, cacheFolder })
				fileWriter.seek(0)
				fileWriter.write(blob)
			})
		},
		e => onFSError(`FS Error getting ${filename} to write: ` + e, { e, cacheFolder }),
		{ create: true }
	)
}

/**
 * Yep, you guessed: reads a file.
 * This is Cordova-only, but runs a mock in case of a web+dev platform.
 * @param filename {string}
 * @param onSuccess {function(string)} Called with the file contents
 * @param onError {function({ code })} If not given, just outputs the error to `console.error()`. See https://cordova.apache.org/docs/en/11.x/reference/cordova-plugin-file/#list-of-error-codes-and-meanings
 * @param cacheFolder {boolean}
 */
export function readFile(filename, onSuccess, onError = null, cacheFolder = true) {
	return getFile(filename, cacheFolder, 'abc 123', fileEntry => {
		fileEntry.file(
			file => {
				const reader     = new FileReader()
				reader.onloadend = function() { onSuccess(this.result) }
				reader.readAsText(file)
			},
			e => onError? onError(e) : onFSError(`FS Error reading ${filename}: ` + e, { e, cacheFolder })
		)
	}, onError)
}

export function removeFile(filename, onSuccess = null, onError = null, cacheFolder = true) {
	return getFile(filename, cacheFolder, true, fileEntry => {
		fileEntry.remove(
			onSuccess,
			e => onError? onError(e) : onFSError(`FS Error erasing ${filename}: ` + e, { e, cacheFolder })
		)
	}, onError)
}

function getFile(filename, cacheFolder, fakeResult, onSuccess, onError = null, mode = {}) {
	if (fsMock(onSuccess, fakeResult)) {
		return
	}

	//there are other folders available in the plugin, but most of them are platform-specific
	const folder = cacheFolder? window.cordova.file.cacheDirectory : window.cordova.file.dataDirectory

	window.resolveLocalFileSystemURL(folder, dir => { // eslint-disable-line no-undef
		dir.getFile(filename, mode, onSuccess,
				e => onError? onError(e) : onFSError(`FS Error ${e.code} resolving file ${folder}${filename}`, e))
	}, e => onError? onError(e) : onFSError('FS Error resolving dir: ' + e, e))
}

function onFSError(msg, details) {
	if (platform.isDev()) {
		window.alert(msg)
	}
	console.error(msg, details)
}

/**
 * Checks if we're able to use filesystem-related Cordova functions, and runs the success function with the given fake result if not.
 * @param success {function}
 * @param fakeResult
 * @returns {boolean} Returns true if you should abort execution.
 */
function fsMock(success, fakeResult) {
	if (!platform.isNative()) {
		if (platform.isDev()) {
			console.warn('Here we go again: mocking Cordova filesystem calls!')
			success(fakeResult) //FIXME: move all those cordova mocks to a single place
		} else {
			console.error('this function is Cordova-only, we cannot write to the filesystem here.')
		}
		return true
	}
	return false
}
